import React, { Component } from 'react'
import { Descriptions, Form, FormInstance, Select, Tag, Typography } from 'antd'
import { DEFAULT_SELECT_FILTER_OPTION } from 'helpers/form-fields.helper'
import MappingStepper, { Step } from '../MappingStepper'
import { ShelterConsumer, ShelterContextData } from 'context/ShelterContext'
import { PetColorKey } from 'context/ColorContext'
import { PartnerKeyType } from 'interfaces/IIntegration'
import { sbListAnimalColor } from 'services/integration'
import ShelterBuddyExternalField from '../ShelterBuddyExternalField'
import { DIRECTION_LABEL, DirectionKey, Flow, Partner, TypeKey } from '../shared/types'
import { resolveDirectionKey, resolveFlow, resolveTypeKey } from '../shared/resolvers'
import { makeSbLoader } from '../shared/sbOptions'
import { ActiveField, IncomingStep, IntegrationField, ManualExternalInputs, PartnerField, PriorityField, ScopeField } from '../shared/fields'
import { DirectionField, ExpressionField, TypeStep } from '../shared/match'

/** Loads the Shelter Buddy color list for an integration. */
const loadColors = makeSbLoader(sbListAnimalColor, 'colors')

type MappingColorFormProps = {
  form: FormInstance
  partnerList: Partner[] | null
  /** Save-in-progress flag, forwarded to the final-step Save button. */
  loading?: boolean
}

type MappingColorFormInnerProps = MappingColorFormProps & {
  shelterContext: ShelterContextData
}

type MappingColorFormInnerState = {
  typeKey: TypeKey
  flow: Flow
  directionKey: DirectionKey
}

class MappingColorFormInner extends Component<MappingColorFormInnerProps, MappingColorFormInnerState> {
  constructor(props: MappingColorFormInnerProps) {
    super(props)
    this.state = {
      typeKey: resolveTypeKey(props.form),
      flow: resolveFlow(props.form, 'color_key'),
      directionKey: resolveDirectionKey(props.form),
    }
  }

  componentDidMount() {
    const { shelterContext, form } = this.props

    // Initial values may settle after the Form mounts — re-resolve everything.
    const typeKey = resolveTypeKey(form)
    const flow = resolveFlow(form, 'color_key')
    const directionKey = resolveDirectionKey(form)

    // Persist the resolved enum defaults so a never-touched new mapping still
    // submits them (the radios below are state-driven, not Form-bound directly).
    form.setFieldsValue({ type_key: typeKey })
    if (typeKey === 'MATCH') form.setFieldsValue({ direction_key: directionKey })
    if (form.getFieldValue('priority') == null) form.setFieldsValue({ priority: 0 })

    if (
      typeKey !== this.state.typeKey ||
      flow !== this.state.flow ||
      directionKey !== this.state.directionKey
    ) {
      this.setState({ typeKey, flow, directionKey })
    }

    if (!shelterContext.fetching && !shelterContext.shelters?.length) {
      shelterContext.requestShelterList({
        options: {
          limit: 9999,
          sort: ['name'],
        },
        search: {
          _fields: ["id", "uuid", "name"],
          active: true,
          deleted: false,
          organization_integrations: {
            with: {
              _fields: ["id", "uuid", "description", "partner_key", "main", "shelter_id"],
              active: true,
              deleted: false,
            }
          }
        }
      })
    }
  }

  /** Resolve a shelter name for display, from the loaded shelter list. */
  shelterName = (uuid?: string) =>
    this.props.shelterContext.shelters?.find(s => s.uuid === uuid)?.name

  /** Resolve a partner display name. */
  partnerName = (key?: string) =>
    this.props.partnerList?.find(p => p.key === key)?.name || key

  // ----- Field groups -----

  /** Adopets color target — used by DIRECT and by MATCH/INPUT. */
  renderColorField = () => {
    const colorsOptions = Object.keys(PetColorKey).sort().map(value => ({
      label: value,
      value: value,
    }))

    return (
      <Form.Item
        label="Color"
        name="color_key"
        rules={[{ required: true, message: 'Missing a color' }]}
      >
        <Select
          showSearch
          filterOption={DEFAULT_SELECT_FILTER_OPTION}
          placeholder="Select a color"
          options={colorsOptions}
        />
      </Form.Item>
    )
  }

  /** External target (Shelter Buddy picker, or name + id inputs) — DIRECT and MATCH/OUTPUT. */
  renderExternalTarget = () => {
    const { form } = this.props

    return (
      <Form.Item
        noStyle
        shouldUpdate={(prev, cur) =>
          prev.shelter_uuid !== cur.shelter_uuid ||
          prev.partner_key !== cur.partner_key ||
          prev.integration_uuid !== cur.integration_uuid
        }
      >
        {({ getFieldValue }) => {
          const shelterUuid: string | undefined = getFieldValue("shelter_uuid")
          const partnerKey: PartnerKeyType | undefined = getFieldValue("partner_key")
          const integrationUuid: string | undefined = getFieldValue("integration_uuid")

          const canLoadExternal =
            !!shelterUuid && !!integrationUuid && partnerKey === "SHELTER_BUDDY"

          if (canLoadExternal) {
            return (
              <ShelterBuddyExternalField
                form={form}
                integrationUuid={integrationUuid!}
                loadExternalList={loadColors}
                label="External color"
                placeholder="Select a color"
              />
            )
          }

          return <ManualExternalInputs />
        }}
      </Form.Item>
    )
  }

  /** DIRECT/inbound only: read-only display of the incoming external value. */
  renderIncoming = () => (
    <IncomingStep
      form={this.props.form}
      partnerList={this.props.partnerList}
      externalRowLabel="External Color"
    />
  )

  // ----- Review (all variants) -----

  renderReview = () => {
    const { form } = this.props
    const { typeKey, directionKey } = this.state

    const partnerKey = form.getFieldValue('partner_key')
    const shelterUuid = form.getFieldValue('shelter_uuid')
    const colorKey = form.getFieldValue('color_key')
    const name = form.getFieldValue('name')
    const externalId = form.getFieldValue('external_id')
    const expression = form.getFieldValue('expression')

    const externalSide = [name, externalId].filter(Boolean).join(' · ') || '—'

    let directionLabel: string
    let mappingSummary: string
    if (typeKey === 'DIRECT') {
      // DIRECT mappings sync both ways — the internal direction is BOTH.
      directionLabel = DIRECTION_LABEL.BOTH
      mappingSummary = `${colorKey || '—'} ↔ ${externalSide}`
    } else {
      directionLabel = DIRECTION_LABEL[directionKey]
      mappingSummary = directionKey === 'INPUT'
        ? `expression → ${colorKey || '—'}`
        : `expression → ${externalSide}`
    }

    return (
      <>
        <Descriptions column={1} bordered size="small" style={{ marginBottom: 16 }}>
          <Descriptions.Item label="Type">{typeKey}</Descriptions.Item>
          <Descriptions.Item label="Direction">{directionLabel}</Descriptions.Item>
          <Descriptions.Item label="Partner">{this.partnerName(partnerKey) || '—'}</Descriptions.Item>
          <Descriptions.Item label="Mapping">{mappingSummary}</Descriptions.Item>
          {typeKey === 'MATCH' && (
            <Descriptions.Item label="Expression">
              <Typography.Text code>{expression || '—'}</Typography.Text>
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Applies to">
            {shelterUuid ? (this.shelterName(shelterUuid) || shelterUuid) : <Tag>GLOBAL</Tag>}
          </Descriptions.Item>
        </Descriptions>

        <PriorityField />
        <ActiveField />
      </>
    )
  }

  // ----- Step definitions -----

  /** Step 1 is always the type picker; the rest depends on type (and DIRECT flow). */
  steps = (): Step[] => {
    const { form, shelterContext } = this.props

    const typeStep: Step = {
      key: 'type',
      title: 'Type',
      fields: [],
      content: () => (
        <TypeStep
          form={form}
          value={this.state.typeKey}
          directionKey={this.state.directionKey}
          onChange={typeKey => this.setState({ typeKey })}
        />
      ),
    }

    const reviewStep: Step = {
      key: 'review',
      title: 'Review & Save',
      fields: ['priority'],
      content: this.renderReview,
    }

    if (this.state.typeKey === 'MATCH') {
      const ruleFields = this.state.directionKey === 'INPUT'
        ? ['expression', 'color_key']
        : ['expression', 'name', 'external_id']

      return [
        typeStep,
        {
          key: 'setup',
          title: 'Setup',
          fields: ['partner_key', 'shelter_uuid'],
          content: () => (
            <>
              <DirectionField
                form={form}
                value={this.state.directionKey}
                onChange={directionKey => this.setState({ directionKey })}
              />
              <PartnerField partnerList={this.props.partnerList} />
              <ScopeField form={form} shelterContext={shelterContext} />
            </>
          ),
        },
        {
          key: 'rule',
          title: 'Rule',
          fields: ruleFields,
          content: () => (
            <>
              <ExpressionField directionKey={this.state.directionKey} descriptionLabel="Expression description" />
              {this.state.directionKey === 'INPUT'
                ? this.renderColorField()
                : (<><IntegrationField shelterContext={shelterContext} />{this.renderExternalTarget()}</>)}
            </>
          ),
        },
        reviewStep,
      ]
    }

    // DIRECT — mirror the species form.
    if (this.state.flow === 'inbound') {
      return [
        typeStep,
        {
          key: 'incoming',
          title: 'Incoming',
          fields: [],
          content: this.renderIncoming,
        },
        {
          key: 'our-side',
          title: 'Our side',
          fields: ['color_key', 'shelter_uuid'],
          content: () => (
            <>
              {this.renderColorField()}
              <ScopeField form={form} shelterContext={shelterContext} />
            </>
          ),
        },
        reviewStep,
      ]
    }

    return [
      typeStep,
      {
        key: 'our-side',
        title: 'Our side',
        fields: ['color_key', 'partner_key', 'shelter_uuid'],
        content: () => (
          <>
            {this.renderColorField()}
            <PartnerField partnerList={this.props.partnerList} />
            <ScopeField form={form} shelterContext={shelterContext} />
          </>
        ),
      },
      {
        key: 'external',
        title: 'External target',
        fields: ['name', 'external_id'],
        content: () => (
          <>
            <IntegrationField shelterContext={shelterContext} />
            {this.renderExternalTarget()}
          </>
        ),
      },
      reviewStep,
    ]
  }

  render() {
    return <MappingStepper form={this.props.form} steps={this.steps()} loading={this.props.loading} />
  }
}

export default class MappingColorForm extends Component<MappingColorFormProps> {
  render() {
    return (
      <ShelterConsumer>
        {(shelterContext) => (
          <MappingColorFormInner
            form={this.props.form}
            partnerList={this.props.partnerList}
            loading={this.props.loading}
            shelterContext={shelterContext}
          />
        )}
      </ShelterConsumer>
    )
  }
}
